import Modal from "@/components/controls/Modal";
import { find, forEach, isNull, isUndefined, values } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Text, { TextVariant } from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import { EntityMapping, TaskData, TaskFormData } from "@/entities/tasks/types";
import {
  EntityType,
  entityTypeToFieldName,
  entityTypeToPrefix
} from "@/services/hooks/entities/useEntityType";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import {
  FormFieldNewType,
  LabelPosition
} from "@/components/controls/Form/types";
import { SingleSelectProps } from "@/components/controls/SingleSelect";
import { useEntitySelect } from "@/services/hooks/entities/useEntitySelect";
import { getUsersOptions } from "@/entities/forms/helpers";
import LinkToMenu, {
  LinkToSource
} from "@/components/layouts/TasksDrawer/LinkToMenu";
import useBoundStore from "@/services/store/BoundStore";
import { usePeople } from "@/entities/people/hooks";
import { useUsersMe } from "@/entities/users/hooks";
import IconContainer from "@/components/ui/icon-container";
import {
  Account,
  Close,
  Contracts,
  Delete,
  Diamond,
  FlagPriority,
  FlagPriorityOutline,
  Magnet,
  User
} from "@dreamhub/designsystem/icons";
import { ObjectEntity } from "@/entities/forms/types";
import UserAvatar from "@/components/controls/UserAvatar";
import {
  useCreateTaskMutation,
  useFilteredTasks,
  useRefreshFilteredTasks,
  useUpdateTaskMutation
} from "@/entities/tasks/hooks";
import { toLocalDate } from "@/utils/datetime";
import { useTaskActions } from "@/services/hooks/entities/tasks/useTaskActions";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import SubmitButton from "@/components/layouts/TasksDrawer/SubmitButton";
import TaskMetadataSubtitle from "@/components/layouts/TasksDrawer/TaskMetadataSubtitle";
import Checkmark from "@/components/ui/checkmark";
import {
  getDefaultEntityIds,
  getEntityTypeById,
  getRelatedToFieldNames,
  buildEntityNameOptions
} from "@/entities/tasks/helpers";
import {
  buildLegacyEntityIds,
  getTaskEntityNames
} from "@/entities/tasks/taskLinks";
import LinkedToField from "@/components/layouts/TasksDrawer/LinkedToField";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useTaskLinks } from "@/components/layouts/TasksDrawer/useTaskLinks";
import { EntityData } from "@/services/api/types";
import toast from "react-hot-toast";
import { ISOStringFormat, isPast, isToday } from "date-fns";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";

const TRANSLATION_KEY = "tasks.createModal";
const HIGH_PRIORITY_OPTION_VALUE = "high";
const LINK_TO_FIELD = "linkTo";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isEditMode: boolean;
  editTaskId: string | null;
  editTaskData?: TaskData;
  entityType?: EntityType;
  entityData?: EntityData;
}

const TaskModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  isEditMode,
  editTaskId,
  editTaskData,
  entityType,
  entityData
}) => {
  const refreshTasks = useRefreshFilteredTasks();
  const users = useBoundStore((state) => state.users.entities);
  const userData = useUsersMe().data;
  const { enabled: showMultiLink } = useFeatureFlag(
    FeatureFlags.CRM_8805_TASK_MULTI_LINK
  );

  const getDefaults = () => {
    if (isEditMode && editTaskData) {
      const findByPrefix = (prefix: string) =>
        editTaskData.entityIds?.find((id) => id.startsWith(prefix)) ?? "";
      return {
        dealId: findByPrefix(entityTypeToPrefix[EntityType.DEAL]),
        leadId: findByPrefix(entityTypeToPrefix[EntityType.LEAD]),
        companyId: findByPrefix(entityTypeToPrefix[EntityType.COMPANY]),
        contactId: findByPrefix(entityTypeToPrefix[EntityType.PERSON]),
        contractId: findByPrefix(entityTypeToPrefix[EntityType.CONTRACT])
      };
    }
    if (!entityType || !entityData) {
      return {
        dealId: "",
        leadId: "",
        companyId: "",
        contactId: "",
        contractId: ""
      };
    }
    return getDefaultEntityIds(entityType, entityData);
  };

  const {
    dealId: defaultDealId,
    leadId: defaultLeadId,
    companyId: defaultCompanyId,
    contactId: defaultContactId,
    contractId: defaultContractId
  } = getDefaults();

  const defaultAssigneeId = isEditMode
    ? editTaskData?.assigneeId ?? userData?.id
    : userData?.id;

  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  const [isFormValid, setIsFormValid] = useState(false);
  const [formSetValue, setFormSetValue] = useState<any>(null);
  const [missingField, setMissingField] = useState<"title" | "dueDate" | null>(
    "title"
  );

  const defaultObjectEntity = isEditMode
    ? defaultDealId
      ? ObjectEntity.DEAL
      : defaultLeadId
        ? ObjectEntity.LEAD
        : defaultContractId
          ? ObjectEntity.CONTRACT
          : null
    : null;

  const [isMarkedAsComplete, setIsMarkedAsComplete] = useState(
    isEditMode ? editTaskData?.isCompleted ?? false : false
  );
  const [objectEntity, setObjectEntity] = useState<ObjectEntity | null>(
    defaultObjectEntity
  );
  const [selectedDealId, setSelectedDealId] = useState(defaultDealId);
  const [selectedLeadId, setSelectedLeadId] = useState(defaultLeadId);
  const [selectedCompanyId, setSelectedCompanyId] = useState(defaultCompanyId);
  const [selectedContactId, setSelectedContactId] = useState(defaultContactId);
  const [selectedContractId, setSelectedContractId] =
    useState(defaultContractId);
  const [selectedAssigneeId, setSelectedAssigneeId] =
    useState(defaultAssigneeId);
  const [selectedDueDate, setSelectedDueDate] = useState<ISOStringFormat | null>(
    isEditMode ? (editTaskData?.dueDate as ISOStringFormat) ?? null : null
  );

  const { entities: selectedPersonData } = usePeople(
    selectedContactId ? [selectedContactId] : []
  );
  const selectedPerson = selectedPersonData?.[0];

  const createTask = useCreateTaskMutation();
  const updateTask = useUpdateTaskMutation();

  const setFormField = (
    field: keyof TaskFormData,
    value: TaskFormData[typeof field]
  ) => {
    formSetValue(field, value, { shouldValidate: true });
  };

  // Use passed-in data for instant display; fall back to fetching if not provided
  const {
    entities: [fetchedTaskData]
  } = useFilteredTasks(
    { id: editTaskId },
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    Boolean(editTaskId) && !editTaskData
  );
  const taskData = editTaskData ?? fetchedTaskData;

  const { isDeleteTaskModalOpen, setIsDeleteTaskModalOpen, handleDeleteTask } =
    useTaskActions({
      task: taskData,
      onDelete: () => setIsOpen(false)
    });
  const entityMapping: EntityMapping = {
    [EntityType.DEAL]: {
      prefix: entityTypeToPrefix[EntityType.DEAL],
      setter: setSelectedDealId,
      field: entityTypeToFieldName[EntityType.DEAL] as keyof TaskFormData
    },
    [EntityType.LEAD]: {
      prefix: entityTypeToPrefix[EntityType.LEAD],
      setter: setSelectedLeadId,
      field: entityTypeToFieldName[EntityType.LEAD] as keyof TaskFormData
    },
    [EntityType.COMPANY]: {
      prefix: entityTypeToPrefix[EntityType.COMPANY],
      setter: setSelectedCompanyId,
      field: entityTypeToFieldName[EntityType.COMPANY] as keyof TaskFormData
    },
    [EntityType.PERSON]: {
      prefix: entityTypeToPrefix[EntityType.PERSON],
      setter: setSelectedContactId,
      field: entityTypeToFieldName[EntityType.PERSON] as keyof TaskFormData
    },
    [EntityType.CONTRACT]: {
      prefix: entityTypeToPrefix[EntityType.CONTRACT],
      setter: setSelectedContractId,
      field: entityTypeToFieldName[EntityType.CONTRACT] as keyof TaskFormData
    }
  };

  useEffect(() => {
    if (isOpen && isEditMode) {
      // Force reset all entity selections when opening in edit mode or switching tasks
      Object.values(entityMapping).forEach(({ setter, field }) => {
        setter?.("");
        if (formSetValue) {
          setFormField(field, "");
        }
      });
    }
  }, [isOpen, isEditMode, editTaskId, formSetValue]);

  useEffect(() => {
    if (!formSetValue) return;
    const { dealId, leadId, companyId, contactId, contractId } =
      !entityType || !entityData || isEditMode
        ? { dealId: "", leadId: "", companyId: "", contactId: "", contractId: "" }
        : getDefaultEntityIds(entityType, entityData);
    const entities = [
      { entity: EntityType.DEAL, value: dealId },
      { entity: EntityType.LEAD, value: leadId },
      { entity: EntityType.COMPANY, value: companyId },
      { entity: EntityType.PERSON, value: contactId },
      { entity: EntityType.CONTRACT, value: contractId }
    ];

    forEach(entities, (entity) => {
      if (entity?.value && entity.entity && entity.entity in entityMapping) {
        const mapping = entityMapping[entity.entity];
        if (mapping) {
          mapping?.setter?.(entity?.value);
          setFormField(mapping?.field, entity?.value);
          // On company, also empty the person, deal, and lead fields
          if (
            entity.entity === EntityType.COMPANY &&
            entityType !== EntityType.PERSON &&
            entityMapping[EntityType.PERSON]?.field
          ) {
            setFormField(entityMapping[EntityType.PERSON]?.field, "");
          }
          if (
            entity.entity === EntityType.COMPANY &&
            entityType !== EntityType.DEAL &&
            entityMapping[EntityType.DEAL]?.field
          ) {
            setFormField(entityMapping[EntityType.DEAL]?.field, "");
          }
          if (
            entity.entity === EntityType.COMPANY &&
            entityType !== EntityType.LEAD &&
            entityMapping[EntityType.LEAD]?.field
          ) {
            setFormField(entityMapping[EntityType.LEAD]?.field, "");
          }
        }
      }
    });
  }, [entityType, entityData, formSetValue]);

  const {
    options: dealOptions,
    onSearch: onDealSearch,
    isLoading: isLoadingDeals,
    searchTerm: dealSearchTerm
  } = useEntitySelect({
    entityType: EntityType.DEAL,
    selectedId: selectedDealId,
    filters: selectedCompanyId ? { companyId: selectedCompanyId } : undefined,
    autoSelectSingleOption: true,
    onAutoSelect: (id) => {
      if (formSetValue && entityMapping[EntityType.DEAL]?.field) {
        setFormField(entityMapping[EntityType.DEAL]?.field, id as string);
      }
    },
    alwaysEnabled: !showMultiLink
  });

  const {
    options: leadOptions,
    onSearch: onLeadSearch,
    isLoading: isLoadingLeads,
    searchTerm: leadSearchTerm
  } = useEntitySelect({
    entityType: EntityType.LEAD,
    selectedId: selectedLeadId,
    filters: selectedCompanyId ? { companyId: selectedCompanyId } : undefined,
    autoSelectSingleOption: true,
    onAutoSelect: (id) => {
      if (formSetValue && entityMapping[EntityType.LEAD]?.field) {
        setFormField(entityMapping[EntityType.LEAD]?.field, id as string);
      }
    },
    alwaysEnabled: !showMultiLink
  });

  const {
    options: companyOptions,
    onSearch: onCompanySearch,
    isLoading: isLoadingCompanies,
    searchTerm: companySearchTerm
  } = useEntitySelect({
    entityType: EntityType.COMPANY,
    selectedId: selectedCompanyId,
    filters: selectedPerson?.companyId
      ? { id: selectedPerson.companyId }
      : undefined,
    alwaysEnabled: !showMultiLink
  });

  const {
    options: personOptions,
    onSearch: onPersonSearch,
    isLoading: isLoadingPeople,
    searchTerm: personSearchTerm
  } = useEntitySelect({
    entityType: EntityType.PERSON,
    selectedId: selectedContactId,
    filters: selectedCompanyId ? { companyId: selectedCompanyId } : undefined,
    alwaysEnabled: !showMultiLink
  });

  const {
    options: contractOptions,
    onSearch: onContractSearch,
    isLoading: isLoadingContracts,
    searchTerm: contractSearchTerm
  } = useEntitySelect({
    entityType: EntityType.CONTRACT,
    selectedId: selectedContractId,
    filters: selectedCompanyId ? { companyId: selectedCompanyId } : undefined,
    alwaysEnabled: !showMultiLink
  });

  // Clear reminders when the reminders field becomes disabled (due date is today or in the past)
  useEffect(() => {
    const isRemindersDisabled =
      !isNull(selectedDueDate) &&
      (isToday(toLocalDate(selectedDueDate)) ||
        isPast(toLocalDate(selectedDueDate)));

    if (isRemindersDisabled && formSetValue) {
      setFormField("reminders", []);
    }
  }, [selectedDueDate, formSetValue]);

  useEffect(() => {
    if (!isOpen) {
      formRef.current?.reset();
      setIsMarkedAsComplete(false);
      setObjectEntity(null);
      setSelectedDueDate(null);

      // In edit mode, reset to empty values, not defaults from entity context
      setSelectedDealId(isEditMode ? "" : defaultDealId);
      setSelectedLeadId(isEditMode ? "" : defaultLeadId);
      setSelectedCompanyId(isEditMode ? "" : defaultCompanyId);
      setSelectedContactId(isEditMode ? "" : defaultContactId);
      setSelectedContractId(isEditMode ? "" : defaultContractId);
      setSelectedAssigneeId(isEditMode ? "" : defaultAssigneeId);
    }
  }, [
    isOpen,
    isEditMode,
    defaultDealId,
    defaultLeadId,
    defaultCompanyId,
    defaultContactId,
    defaultContractId,
    defaultAssigneeId
  ]);

  useEffect(() => {
    if (selectedPerson?.companyId && selectedContactId) {
      setSelectedCompanyId(selectedPerson.companyId);
      if (formSetValue && entityMapping[EntityType.COMPANY]?.field) {
        setFormField(
          entityMapping[EntityType.COMPANY]?.field,
          selectedPerson.companyId
        );
      }
    }
  }, [selectedPerson, selectedContactId, formSetValue]);

  const {
    links,
    namedLinks,
    suggestions,
    originLink,
    addLink: handleAddLink,
    removeLink: handleRemoveLink
  } = useTaskLinks({
    enabled: showMultiLink,
    isOpen,
    isEditMode,
    editTaskId,
    taskData,
    entityType,
    entityData
  });

  const handleSetValue = (formMethods: { setValue: any; getValues: any }) => {
    if (typeof formMethods.setValue === "function") {
      setFormSetValue(() => formMethods.setValue);
    }
  };

  const getTransformedTaskData = (data: TaskFormData) => {
    const entityIdFields = [
      entityMapping[EntityType.DEAL]?.field,
      entityMapping[EntityType.LEAD]?.field,
      entityMapping[EntityType.COMPANY]?.field,
      entityMapping[EntityType.PERSON]?.field,
      entityMapping[EntityType.CONTRACT]?.field
    ];

    const entityIds = showMultiLink
      ? links.map((link) => link.entityId)
      : buildLegacyEntityIds(
          taskData?.entityIds,
          entityIdFields.map((field) => {
            const value = field ? data[field as keyof TaskFormData] : undefined;
            return typeof value === "string" ? value : undefined;
          })
        );

    const isHighPriority = data.isHighPriority === HIGH_PRIORITY_OPTION_VALUE;
    const reminders = data.reminders ? { items: data.reminders } : null;

    const attachments = data.attachments ? { items: data.attachments } : null;

    const dataCopy = { ...data };

    entityIdFields.forEach((field) => {
      delete dataCopy[field as keyof typeof dataCopy];
    });
    delete dataCopy[LINK_TO_FIELD];

    const transformedTaskData: Omit<TaskData, "id"> = {
      ...dataCopy,
      entityIds,
      isHighPriority,
      reminders,
      attachments,
      isCompleted: isMarkedAsComplete
    };
    return transformedTaskData;
  };

  const handleSubmit = async (data: TaskFormData) => {
    const taskData = getTransformedTaskData(data);
    if (editTaskId && isEditMode) {
      updateTask.mutate(
        {
          pathParams: { taskId: editTaskId },
          data: taskData
        },
        {
          onSuccess: () => {
            setIsOpen(false);
            refreshTasks();
          },
          onError: () => {
            toast.error(t(`${TRANSLATION_KEY}.updateError`));
          }
        }
      );
    } else {
      createTask.mutate(
        {
          data: taskData
        },
        {
          onSuccess: () => {
            refreshTasks();
            setIsOpen(false);
            toast.success(
              <ToastBody
                title={t(`${TRANSLATION_KEY}.successToast`)}
                actions={
                  <IconButton
                    size="md"
                    variant="iconNoBg"
                    iconColor="secondary"
                    onClick={() => toast.dismiss()}
                  >
                    <Close />
                  </IconButton>
                }
              />,
              {
                duration: 5000
              }
            );
          },
          onError: () => {
            toast.error(t(`${TRANSLATION_KEY}.createError`));
          }
        }
      );
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  const entityNameOptions = useMemo(
    () =>
      buildEntityNameOptions(
        getTaskEntityNames(taskData?.entityNames, taskData?.entities),
        taskData?.entityIds
      ),
    [taskData?.entityNames, taskData?.entities, taskData?.entityIds]
  );

  const withEntityNameOption = (
    options: { id: string | number | null; displayName: string }[],
    prefix: string
  ) => {
    const fallback = entityNameOptions[prefix];
    if (!fallback) return options;
    if (options.some((option) => option.id === fallback.id)) return options;
    return [fallback, ...options];
  };

  const objectEntityToEntityType: Record<ObjectEntity, EntityType> = {
    [ObjectEntity.DEAL]: EntityType.DEAL,
    [ObjectEntity.LEAD]: EntityType.LEAD,
    [ObjectEntity.CONTRACT]: EntityType.CONTRACT
  };

  const linkToSources: LinkToSource[] = [
    {
      entity: ObjectEntity.DEAL,
      label: t("objectEntities.deal"),
      options: withEntityNameOption(
        dealOptions,
        entityMapping[EntityType.DEAL]?.prefix ?? ""
      ),
      searchTerm: dealSearchTerm,
      onSearch: onDealSearch,
      isLoading: isLoadingDeals
    },
    {
      entity: ObjectEntity.LEAD,
      label: t("objectEntities.lead"),
      options: withEntityNameOption(
        leadOptions,
        entityMapping[EntityType.LEAD]?.prefix ?? ""
      ),
      searchTerm: leadSearchTerm,
      onSearch: onLeadSearch,
      isLoading: isLoadingLeads
    },
    {
      entity: ObjectEntity.CONTRACT,
      label: t("objectEntities.contract"),
      options: withEntityNameOption(
        contractOptions,
        entityMapping[EntityType.CONTRACT]?.prefix ?? ""
      ),
      searchTerm: contractSearchTerm,
      onSearch: onContractSearch,
      isLoading: isLoadingContracts
    }
  ];

  const linkToSelectedId =
    (objectEntity === ObjectEntity.DEAL
      ? selectedDealId
      : objectEntity === ObjectEntity.LEAD
        ? selectedLeadId
        : objectEntity === ObjectEntity.CONTRACT
          ? selectedContractId
          : "") ?? "";

  const onLinkToSelect = (entity: ObjectEntity, id: string) => {
    [EntityType.DEAL, EntityType.LEAD, EntityType.CONTRACT].forEach((type) => {
      entityMapping[type]?.setter?.("");
      if (formSetValue && entityMapping[type]?.field) {
        setFormField(entityMapping[type].field, "");
      }
    });
    const type = objectEntityToEntityType[entity];
    entityMapping[type]?.setter?.(id);
    if (formSetValue && entityMapping[type]?.field) {
      setFormField(entityMapping[type].field, id);
    }
    setObjectEntity(entity);
  };

  const onLinkToClear = () => {
    const type = objectEntity ? objectEntityToEntityType[objectEntity] : null;
    if (type) {
      entityMapping[type]?.setter?.("");
      if (formSetValue && entityMapping[type]?.field) {
        setFormField(entityMapping[type].field, "");
      }
    }
    setObjectEntity(null);
  };

  const allFields: { [key: string]: FormFieldNewType } = {
    linkTo: {
      name: LINK_TO_FIELD,
      type: "render",
      required: false,
      layout: "flex",
      fieldContainerClassName: "min-h-0",
      renderNode: (
        <LinkToMenu
          sources={linkToSources}
          selectedEntity={objectEntity}
          selectedId={linkToSelectedId}
          placeholder={t(`${TRANSLATION_KEY}.linkTo`)}
          onSelect={onLinkToSelect}
          onClear={onLinkToClear}
        />
      )
    },
    dealId: {
      name: "dealId",
      type: "singleSelect",
      required: false,
      defaultValue: selectedDealId,
      layout: "flex",
      fieldContainerClassName: "min-h-0",
      fieldActionProps: {
        options: withEntityNameOption(dealOptions, entityMapping[EntityType.DEAL]?.prefix ?? ""),
        placeholder: t(`${TRANSLATION_KEY}.dealId`),
        showSearch: true,
        initialSearchTerm: dealSearchTerm,
        onSearch: onDealSearch,
        isLoading: isLoadingDeals,
        variant: "chip",
        onClear: () => {
          setSelectedDealId("");
          if (formSetValue && entityMapping[EntityType.DEAL]?.field) {
            setFormField(entityMapping[EntityType.DEAL]?.field, "");
          }
        },
        popoverTriggerClassName: "border-transparent !p-1 min-h-fit",
        placeholderClassName: "p-0",
        selectedVariant: "bodySecondary",
        popoverClassName: "min-w-56",
        triggerOnlyIcon: (
          <IconContainer color="secondary">
            <Diamond />
          </IconContainer>
        ),
        mainContainerClassName: "!shadow-none"
      }
    },
    leadId: {
      name: "leadId",
      type: "singleSelect",
      required: false,
      defaultValue: selectedLeadId,
      layout: "flex",
      fieldContainerClassName: "min-h-0",
      fieldActionProps: {
        options: withEntityNameOption(leadOptions, entityMapping[EntityType.LEAD]?.prefix ?? ""),
        placeholder: t(`${TRANSLATION_KEY}.leadId`),
        showSearch: true,
        initialSearchTerm: leadSearchTerm,
        onSearch: onLeadSearch,
        isLoading: isLoadingLeads,
        variant: "chip",
        onClear: () => {
          setSelectedLeadId("");
          if (formSetValue && entityMapping[EntityType.LEAD]?.field) {
            setFormField(entityMapping[EntityType.LEAD]?.field, "");
          }
        },
        popoverTriggerClassName: "border-transparent !p-1 min-h-fit",
        placeholderClassName: "p-0",
        selectedVariant: "bodySecondary",
        popoverClassName: "min-w-56",
        triggerOnlyIcon: (
          <IconContainer color="secondary">
            <Magnet />
          </IconContainer>
        ),
        mainContainerClassName: "!shadow-none"
      }
    },
    companyId: {
      name: "companyId",
      type: "singleSelect",
      required: false,
      defaultValue: selectedCompanyId,
      layout: "flex",
      fieldContainerClassName: "min-h-0",
      fieldActionProps: {
        options: withEntityNameOption(companyOptions, entityMapping[EntityType.COMPANY]?.prefix ?? ""),
        placeholder: t(`${TRANSLATION_KEY}.companyId`),
        showSearch: true,
        initialSearchTerm: companySearchTerm,
        onSearch: onCompanySearch,
        isLoading: isLoadingCompanies,
        variant: "chip",
        onClear: () => {
          setSelectedCompanyId("");
          if (formSetValue && entityMapping[EntityType.COMPANY]?.field) {
            setFormField(entityMapping[EntityType.COMPANY]?.field, "");
          }
        },
        popoverTriggerClassName: "border-transparent !p-1 min-h-fit",
        placeholderClassName: "p-0",
        selectedVariant: "bodySecondary",
        popoverClassName: "min-w-56",
        onSelection: (value: string | number | null) => {
          setSelectedCompanyId(value as string);
          if (formSetValue) {
            if (entityMapping[EntityType.PERSON]?.field) {
              setSelectedContactId("");
              setFormField(entityMapping[EntityType.PERSON]?.field, "");
            }
            if (entityMapping[EntityType.DEAL]?.field) {
              setSelectedDealId("");
              setFormField(entityMapping[EntityType.DEAL]?.field, "");
            }
            if (entityMapping[EntityType.LEAD]?.field) {
              setSelectedLeadId("");
              setFormField(entityMapping[EntityType.LEAD]?.field, "");
            }
          }
        },
        triggerOnlyIcon: (
          <IconContainer color="secondary">
            <Account />
          </IconContainer>
        ),
        mainContainerClassName: "!shadow-none"
      }
    },
    contactId: {
      name: "contactId",
      type: "singleSelect",
      required: false,
      defaultValue: selectedContactId,
      layout: "flex",
      fieldContainerClassName: "min-h-0",
      fieldActionProps: {
        options: withEntityNameOption(personOptions, entityMapping[EntityType.PERSON]?.prefix ?? ""),
        placeholder: t(`${TRANSLATION_KEY}.contactId`),
        showSearch: true,
        initialSearchTerm: personSearchTerm,
        onSearch: onPersonSearch,
        isLoading: isLoadingPeople,
        variant: "chip",
        onClear: () => {
          setSelectedContactId("");
          if (formSetValue && entityMapping[EntityType.PERSON]?.field) {
            setFormField(entityMapping[EntityType.PERSON]?.field, "");
          }
        },
        popoverTriggerClassName: "border-transparent !p-1 min-h-fit",
        placeholderClassName: "p-0",
        selectedVariant: "bodySecondary",
        popoverClassName: "min-w-56",
        onSelection: (value: string | number | null) => {
          setSelectedContactId(value as string);
        },
        triggerOnlyIcon: (
          <IconContainer color="secondary">
            <User />
          </IconContainer>
        ),
        showSelectedItemSubtitle: false,
        mainContainerClassName: "!shadow-none"
      }
    },
    contractId: {
      name: "contractId",
      type: "singleSelect",
      required: false,
      defaultValue: selectedContractId,
      layout: "flex",
      fieldContainerClassName: "min-h-0",
      fieldActionProps: {
        options: withEntityNameOption(
          contractOptions,
          entityMapping[EntityType.CONTRACT]?.prefix ?? ""
        ),
        placeholder: t(`${TRANSLATION_KEY}.contractId`),
        showSearch: true,
        initialSearchTerm: contractSearchTerm,
        onSearch: onContractSearch,
        isLoading: isLoadingContracts,
        variant: "chip",
        onClear: () => {
          setSelectedContractId("");
          if (formSetValue && entityMapping[EntityType.CONTRACT]?.field) {
            setFormField(entityMapping[EntityType.CONTRACT]?.field, "");
          }
        },
        onSelection: (value: string | number | null) => {
          setSelectedContractId(value as string);
        },
        popoverTriggerClassName: "border-transparent !p-1 min-h-fit",
        placeholderClassName: "p-0",
        selectedVariant: "bodySecondary",
        popoverClassName: "min-w-56",
        triggerOnlyIcon: (
          <IconContainer color="secondary">
            <Contracts />
          </IconContainer>
        ),
        mainContainerClassName: "!shadow-none"
      }
    },
    title: {
      name: "title",
      type: "text",
      required: true,
      showRequiredIndicator: false,
      fieldActionProps: {
        placeholder: t(`${TRANSLATION_KEY}.title`),
        className:
          "bg-white-4 p-2 pl-3 border-transparent text-xl font-semibold !shadow-none"
      }
    },
    description: {
      name: "description",
      type: "textarea",
      required: false,
      fieldActionProps: {
        placeholder: t(`${TRANSLATION_KEY}.description`),
        className:
          "bg-white-4 p-2 pl-3 border-transparent text-base transition-all duration-300 hover:border-border-field-hover focus:border-border-field-focus !shadow-none",
        minRows: 12
      }
    },
    assigneeId: {
      name: "assigneeId",
      type: "singleSelect",
      required: true,
      showRequiredIndicator: false,
      defaultValue: defaultAssigneeId,
      layout: "flex",
      fieldActionProps: {
        options: getUsersOptions(users, userData?.id, t),
        placeholder: t(`${TRANSLATION_KEY}.assigneeId`),
        showSearch: true,
        variant: "badge",
        renderIcon: (userId: string | number | null) => (
          <UserAvatar userData={users[userId as string]} size="M" />
        ),
        hideChevron: true,
        popoverClassName: "gap-0 p-1 !min-w-[200px]",
        popOverHeader: (
          <Text variant="bodySecondary" className="p-1">
            {t(`${TRANSLATION_KEY}.assignTo`)}
          </Text>
        ),
        onSelection: (value: string | number | null) =>
          setSelectedAssigneeId(value as string)
      }
    },
    dueDate: {
      name: "dueDate",
      type: "date",
      required: true,
      showRequiredIndicator: false,
      layout: "flex",
      fieldActionProps: {
        placeholder: t(`${TRANSLATION_KEY}.dueDate`),
        iconPosition: "left",
        isDueDate: true,
        showDatePresets: true,
        className: "gap-2 pl-2",
        onChange: (value: string) =>
          setSelectedDueDate(value as ISOStringFormat)
      }
    },
    reminders: {
      name: "reminders",
      type: "reminders",
      required: false,
      layout: "flex",
      isHidden:
        !isNull(selectedDueDate) &&
        (isToday(toLocalDate(selectedDueDate)) ||
          isPast(toLocalDate(selectedDueDate))),
      fieldActionProps: {
        placeholder: t(`${TRANSLATION_KEY}.reminders`),
        showOnlyNotifyAssignee: selectedAssigneeId !== userData?.id,
        shouldOnlyNotifyAssignee: taskData?.shouldOnlyNotifyAssignee,
        className: "pl-2"
      }
    },
    isHighPriority: {
      name: "isHighPriority",
      type: "singleSelect",
      required: false,
      defaultValue: null,
      layout: "flex",
      fieldActionProps: {
        hideChevron: true,
        showClearButton: true,
        placeholderClassName: "!text-tertiary",
        showPlaceholderIcon: true,
        options: [
          {
            id: HIGH_PRIORITY_OPTION_VALUE,
            displayName: t(`${TRANSLATION_KEY}.priorityOptions.high`)
          }
        ],
        placeholder: t(`${TRANSLATION_KEY}.isHighPriority`),
        variant: "badge",
        renderIcon: (optionId: string | number | null) => (
          <IconContainer
            size="sm"
            color={
              optionId === HIGH_PRIORITY_OPTION_VALUE ? "error" : "secondary"
            }
          >
            {optionId === HIGH_PRIORITY_OPTION_VALUE ? (
              <FlagPriority />
            ) : (
              <FlagPriorityOutline />
            )}
          </IconContainer>
        ),
        popoverClassName: "gap-0 p-1 !min-w-[200px]"
      }
    },
    attachments: {
      name: "attachments",
      type: "attachments",
      required: false,
      layout: "flex",
      fieldActionProps: {
        placeholder: t(`${TRANSLATION_KEY}.attachments`),
        className: "max-w-[10rem]",
        // This slot sits in the modal's metadata strip alongside the other
        // chips: there is room for the stored files, but not for an upload
        // affordance beside them — neither the Small upload button nor the
        // Large drag-and-drop area — so the picker opens over the field.
        variant: "popover"
      }
    }
  };

  // In edit mode, derive layout from the task's own entities (deal > lead priority),
  // not the page context
  const getPrimaryEntityType = () => {
    if (!taskData?.entityIds) return undefined;
    const prefixPriority = [EntityType.DEAL, EntityType.LEAD, EntityType.CONTRACT, EntityType.COMPANY, EntityType.PERSON];
    for (const type of prefixPriority) {
      const prefix = entityMapping[type]?.prefix;
      if (prefix && taskData.entityIds.some((id) => id.startsWith(prefix))) {
        return type;
      }
    }
    return undefined;
  };

  const effectiveEntityType = isEditMode
    ? getPrimaryEntityType()
    : entityType || getEntityTypeById(taskData?.entityIds?.[0], entityMapping);

  const relatedToFieldNames = getRelatedToFieldNames(
    effectiveEntityType,
    entityMapping,
    LINK_TO_FIELD
  );

  // Build form sections with proper field configurations
  const formSections: FormSectionProps[] = useMemo(() => {
    // Build related-to fields with extra props
    const relatedToFields: FormFieldNewType[] = relatedToFieldNames.map(
      (fieldName, index) => {
        const field = { ...allFields[fieldName] };
        const filledValueClassName =
          relatedToFieldNames?.length === 3
            ? "w-[7rem] !p-0"
            : "w-[3.5rem] !p-0";

        if (field.type === "singleSelect" && field.fieldActionProps) {
          field.fieldActionProps = {
            ...(field.fieldActionProps as SingleSelectProps),
            filledValueClassName
          } as SingleSelectProps;
        }

        // First field gets the label
        if (index === 0) {
          return {
            ...field,
            label: t(`${TRANSLATION_KEY}.linkedTo`),
            labelPosition: "left" as LabelPosition,
            labelVariant: "lgBodyTertiary" as TextVariant,
            fieldClassName: "border-transparent items-center",
            labelClassName: "p-0 w-fit"
          };
        }

        return field;
      }
    );

    // Main content fields (title, description)
    const mainFields: FormFieldNewType[] = [
      allFields.title,
      allFields.description
    ];

    // Metadata fields (assignee, due date, reminders, priority, attachments)
    const metadataFields: FormFieldNewType[] = [
      allFields.assigneeId,
      allFields.dueDate,
      allFields.reminders,
      allFields.isHighPriority,
      allFields.attachments
    ].filter((f) => !f.isHidden);

    const linkedToSection: FormSectionProps = showMultiLink
      ? {
          id: "linked-to",
          fields: [
            {
              name: LINK_TO_FIELD,
              type: "render",
              required: false,
              layout: "flex",
              fieldContainerClassName: "min-h-0",
              label: t(`${TRANSLATION_KEY}.linkedTo`),
              labelPosition: "left" as LabelPosition,
              labelVariant: "lgBodyTertiary" as TextVariant,
              fieldClassName: "border-transparent items-start",
              labelClassName: "p-0 w-fit",
              renderNode: (
                <LinkedToField
                  links={namedLinks}
                  suggestions={suggestions}
                  originName={originLink?.entityName}
                  onAdd={handleAddLink}
                  onRemove={handleRemoveLink}
                />
              )
            }
          ],
          fieldsContainerClassName:
            "flex gap-2 flex-row items-center overflow-hidden",
          sectionClassName: "gap-0"
        }
      : {
          id: "linked-to",
          fields: relatedToFields,
          fieldsContainerClassName:
            "flex gap-2 flex-row items-end overflow-hidden",
          sectionClassName: "gap-0"
        };

    return [
      linkedToSection,
      {
        id: "main-fields",
        fields: mainFields,
        sectionClassName: "gap-1",
        fieldsGapClassName: "gap-1"
      },
      {
        id: "metadata",
        fields: metadataFields,
        fieldsContainerClassName: "flex gap-2 flex-row items-end flex-wrap",
        sectionClassName: "gap-0"
      }
    ];
  }, [
    showMultiLink,
    namedLinks,
    suggestions,
    handleAddLink,
    handleRemoveLink,
    originLink?.entityName,
    relatedToFieldNames,
    allFields,
    t,
    dealOptions,
    leadOptions,
    companyOptions,
    personOptions,
    contractOptions,
    selectedDealId,
    selectedLeadId,
    selectedCompanyId,
    selectedContactId,
    selectedContractId,
    selectedAssigneeId,
    selectedDueDate,
    objectEntity,
    users,
    userData,
    taskData,
    entityNameOptions,
    formSetValue
  ]);

  const setEditModeFormFields = () => {
    if (!isEditMode || isUndefined(taskData) || !formSetValue) return;
    // Guard against stale cached data from a previous task
    if (editTaskId && taskData?.id !== editTaskId) return;

    const getEntityIdByPrefix = (prefix: string) =>
      find(taskData?.entityIds, (entityId) => entityId.startsWith(prefix));

    forEach(values(entityMapping), ({ prefix, setter, field }) => {
      const id = getEntityIdByPrefix(prefix);
      setter?.(id ?? "");
      setFormField(field, id ?? "");
    });

    if (entityMapping[EntityType.DEAL] && entityMapping[EntityType.LEAD]) {
      const taskDealId = getEntityIdByPrefix(
        entityMapping[EntityType.DEAL].prefix
      );
      const taskLeadId = getEntityIdByPrefix(
        entityMapping[EntityType.LEAD].prefix
      );
      const taskContractId = entityMapping[EntityType.CONTRACT]
        ? getEntityIdByPrefix(entityMapping[EntityType.CONTRACT].prefix)
        : undefined;
      const taskObjectEntity = taskDealId
        ? ObjectEntity.DEAL
        : taskLeadId
          ? ObjectEntity.LEAD
          : taskContractId
            ? ObjectEntity.CONTRACT
            : null;

      setObjectEntity(taskObjectEntity);
      setFormField(LINK_TO_FIELD, taskObjectEntity);
    }

    setSelectedAssigneeId(taskData?.assigneeId);
    formSetValue("assigneeId", taskData?.assigneeId, {
      shouldValidate: true
    });

    setFormField("title", taskData?.title);
    setFormField("description", taskData?.description ?? "");
    setFormField("dueDate", taskData?.dueDate);
    setSelectedDueDate((taskData?.dueDate as ISOStringFormat) ?? null);
    setFormField("reminders", taskData?.reminders?.items);
    setFormField(
      "isHighPriority",
      taskData?.isHighPriority ? HIGH_PRIORITY_OPTION_VALUE : null
    );
    setFormField("attachments", taskData?.attachments?.items);
    setIsMarkedAsComplete(taskData?.isCompleted);
  };

  useEffect(() => {
    setEditModeFormFields();
  }, [isEditMode, taskData, formSetValue]);

  const titleFocusRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isOpen) {
      titleFocusRef.current?.focus();
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={setIsOpen}
        title={
          <div className="flex items-start gap-4 w-full">
            <div className="flex flex-col gap-1 flex-1">
              <Text as="div" variant="lgSubtitle">
                {t(
                  `${TRANSLATION_KEY}.${isEditMode ? "editTask" : "modalTitle"}`
                )}
              </Text>
              {isEditMode && taskData && (
                <TaskMetadataSubtitle taskData={taskData} />
              )}
            </div>
            {isEditMode && (
              <IconButton
                variant="iconNoBg"
                size="lg"
                iconColor="primary"
                onClick={() => setIsDeleteTaskModalOpen(true)}
              >
                <Delete />
              </IconButton>
            )}
          </div>
        }
        content={
          <div className="flex flex-col gap-4 p-5 w-[45rem]">
            <div ref={titleFocusRef} tabIndex={0} className="sr-only" />
            <FormNew
              className="space-y-2"
              sections={formSections}
              isFormValid={(isValid, watchAllFields) => {
                setIsFormValid(isValid && watchAllFields?.title.trim() !== "");
                setMissingField(
                  !isValid
                    ? watchAllFields?.title.trim() === ""
                      ? "title"
                      : "dueDate"
                    : null
                );
              }}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={handleSetValue}
            />
          </div>
        }

        footer={
          <div className="flex w-full justify-between items-center">
            <Checkmark
              id="isCompleted"
              label={t(`${TRANSLATION_KEY}.markAsComplete`)}
              checked={isMarkedAsComplete}
              onCheckedChange={(checked) => setIsMarkedAsComplete(checked)}
            />
            <div className="flex gap-2">
              <Button
                data-test="cancel-button"
                variant="secondary"
                onClick={handleCancel}
              >
                {t("cancel")}
              </Button>
              <SubmitButton
                tKey={TRANSLATION_KEY}
                disabled={!isFormValid || createTask.isPending || updateTask.isPending}
                onClick={handleCreateNew}
                isEditMode={isEditMode}
                isMarkedAsComplete={isMarkedAsComplete}
                wasOriginallyCompleted={isEditMode && taskData?.isCompleted}
                tooltipText={
                  !isFormValid && missingField
                    ? t(`${TRANSLATION_KEY}.disabledTooltip`, {
                        field: t(
                          `${TRANSLATION_KEY}.missingFields.${missingField}`
                        )
                      })
                    : undefined
                }
              />
            </div>
          </div>
        }
      />
      {isEditMode && (
        <ConfirmationModal
          isOpen={isDeleteTaskModalOpen}
          setIsOpen={setIsDeleteTaskModalOpen}
          title={t("tasks.drawer.task.deleteConfirmation.title")}
          confirmationText={t(
            "tasks.drawer.task.deleteConfirmation.confirmation"
          )}
          handleSubmit={handleDeleteTask}
        />
      )}
    </>
  );
};

export default TaskModal;
